import {
  fetchCollectionTitles,
  createAndPushKlaviyoItem
} from 'rebuy-klaviyo-utils';
import {
  normalizeTags,
  getSaleSettings,
  hasSaleTag
} from 'rebuy-product-utils';
import { html, safeUrl, safeImageUrl } from 'html-utils';

window.BSS = window.BSS || {};
BSS.Components = BSS.Components || {};

/**
 * Defines the `BSS.Components.CartUpsells` module which fetches cart upsell products
 * from Rebuy and renders them as a carousel in the quick cart
 * @module BSS.Components.CartUpsells
 * @returns {Object} An object exposing public methods for cart upsells
 */
BSS.Components.CartUpsells = (function () {
  // Rebuy API configuration
  const REBUY_API_KEY = 'e56408442ba7c8ecb8e58312f7bdf10fd7471281';
  const REBUY_DATA_SOURCE_ID = '247894';
  const REBUY_BASE_URL = `https://rebuyengine.com/api/v1/custom/id/${REBUY_DATA_SOURCE_ID}?key=${REBUY_API_KEY}`;
  const REBUY_WIDGET_ID = '249372';

  // Track if initialization is in progress to prevent duplicate calls
  let isInitializing = false;

  /**
   * Optimizes Shopify CDN image URL by appending width parameter
   * Reduces bandwidth while maintaining retina quality (700px covers 328x2 for desktop)
   * @param {string} src - Original image URL
   * @param {number} width - Desired width in pixels
   * @returns {string} Optimized URL or original if not a Shopify CDN URL
   */
  function optimizeImageUrl(src, width = 700) {
    if (!src || !src.includes('cdn.shopify.com')) return src;
    const separator = src.includes('?') ? '&' : '?';
    return `${src}${separator}width=${width}`;
  }

  /**
   * Maps Rebuy product data to the format expected by the product-card component
   * @param {Object} rebuyProduct - Product data from Rebuy API
   * @returns {Promise<Object>} Mapped product data
   */
  async function mapRebuyProductToProduct(rebuyProduct) {
    if (!rebuyProduct) return null;

    // Ensure we have required data
    if (!rebuyProduct.variants || rebuyProduct.variants.length === 0) {
      console.warn('Product missing variants:', rebuyProduct);
      return null;
    }

    // Fetch collection titles from collection_ids using Shopify Storefront API for Klaviyo integration
    let collections = [];
    if (
      Array.isArray(rebuyProduct.collection_ids) &&
      rebuyProduct.collection_ids.length > 0
    ) {
      collections = await fetchCollectionTitles(rebuyProduct);
    }

    // Filter out discontinued variants
    const availableVariants = rebuyProduct.variants.filter(
      variant => !variant.title.toLowerCase().includes('discontinued')
    );

    if (availableVariants.length === 0) {
      console.warn('Product has no available variants:', rebuyProduct);
      return null;
    }

    // Create image mapping for quick variant lookups
    const variantImageMap = new Map();

    // Build map from variant ID to optimized image URL
    rebuyProduct.variants?.forEach(variant => {
      if (variant.image_id && rebuyProduct.images) {
        const variantImage = rebuyProduct.images.find(
          img => img.id === variant.image_id
        );
        if (variantImage) {
          variantImageMap.set(variant.id, optimizeImageUrl(variantImage.src));
        }
      }
    });

    // If no direct image_id mapping, try variant_ids arrays
    if (variantImageMap.size === 0 && rebuyProduct.images) {
      rebuyProduct.images.forEach(image => {
        if (image.variant_ids && Array.isArray(image.variant_ids)) {
          image.variant_ids.forEach(variantId => {
            if (!variantImageMap.has(variantId)) {
              variantImageMap.set(variantId, optimizeImageUrl(image.src));
            }
          });
        }
      });
    }

    // Also check media array
    if (rebuyProduct.media) {
      rebuyProduct.media.forEach(media => {
        if (
          media.variant_ids &&
          Array.isArray(media.variant_ids) &&
          media.preview_image?.src
        ) {
          media.variant_ids.forEach(variantId => {
            if (!variantImageMap.has(variantId)) {
              variantImageMap.set(
                variantId,
                optimizeImageUrl(media.preview_image.src)
              );
            }
          });
        }
      });
    }

    const product = {
      id: rebuyProduct?.id,
      title: rebuyProduct?.title,
      handle: rebuyProduct?.handle,
      vendor: rebuyProduct?.vendor,
      tags: normalizeTags(rebuyProduct),
      price: parseFloat(availableVariants[0]?.price || 0) * 100,
      compare_at_price: availableVariants[0]?.compare_at_price
        ? parseFloat(availableVariants[0].compare_at_price) * 100
        : null,
      featured_image: optimizeImageUrl(
        rebuyProduct?.image?.src || rebuyProduct?.images?.[0]?.src
      ),
      collections: collections,
      variants: availableVariants.map(variant => ({
        id: variant.id,
        title: variant.title,
        available: variant.inventory_quantity > 0,
        price: parseFloat(variant.price) * 100,
        compare_at_price: variant.compare_at_price
          ? parseFloat(variant.compare_at_price) * 100
          : null,
        featured_image_url: variantImageMap.get(variant.id) || null
      })),
      properties: {
        _attribution: `Product Discovery`,
        _source: `Rebuy`,
        _widget_id: `${REBUY_WIDGET_ID}`
      },
      url: `/products/${encodeURIComponent(rebuyProduct?.handle ?? '')}?_rdiscovery-handle=${encodeURIComponent(rebuyProduct?.handle ?? '')}&_rdiscovery-widget=${REBUY_WIDGET_ID}`,
      selected_or_first_available_variant: {
        url: `/products/${encodeURIComponent(rebuyProduct?.handle ?? '')}?variant=${encodeURIComponent(availableVariants[0]?.id ?? '')}&_rdiscovery-handle=${encodeURIComponent(rebuyProduct?.handle ?? '')}&_rdiscovery-widget=${REBUY_WIDGET_ID}`
      },
      variantImageMap: variantImageMap
    };
    return product;
  }

  /**
   * Applies sale pricing logic to product data
   * @param {Object} product - Mapped product data
   * @returns {Object} Product data with sale pricing applied
   */
  function applySaleLogic(product) {
    // Get sale settings from global settings
    const saleSettings = getSaleSettings();

    if (!saleSettings.isEnabled) {
      return product;
    }

    if (hasSaleTag(product, saleSettings)) {
      const discountPercentage = saleSettings.discountPercentage / 100;
      const discountMultiplier = 1 - discountPercentage;

      product.sale_price = Math.round(product.price * discountMultiplier);

      if (!product.compare_at_price) {
        product.compare_at_price = product.price;
      }

      // Apply to all variants
      product.variants = product.variants.map(variant => ({
        ...variant,
        sale_price: Math.round(variant.price * discountMultiplier),
        compare_at_price: variant.compare_at_price || variant.price
      }));
    }

    return product;
  }

  /**
   * Gets the first cart item's product ID for recommendations
   * @returns {string|null} The first product ID or null if cart is empty
   */
  function getFirstCartItemKey() {
    const firstCartItem = document.querySelector(
      '.quick-cart-item:not(.quick-cart-item--gift)'
    );
    if (!firstCartItem) return null;

    return firstCartItem.getAttribute('data-product-id');
  }

  /**
   * Fetches cart upsell products from Rebuy
   * @returns {Promise<Array>} Array of product recommendations with original Rebuy data
   */
  async function fetchUpsells() {
    try {
      // Get the first cart item's key
      const firstItemKey = getFirstCartItemKey();

      // Build URL with shopify_product_ids query param if available
      let url = `${REBUY_BASE_URL}&format=pretty&limit=5`;

      if (firstItemKey) {
        url += `&shopify_product_ids=${firstItemKey}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Rebuy API error: ${response.statusText}`);
      }

      const response_data = await response.json();
      const products = response_data?.data || [];

      if (!Array.isArray(products) || products.length === 0) {
        return [];
      }

      // Map and apply sale logic to products (parallel processing with individual error handling)
      const mappedProducts = (
        await Promise.all(
          products.map(async rebuyProduct => {
            try {
              const product = await mapRebuyProductToProduct(rebuyProduct);
              return product ? applySaleLogic(product) : null;
            } catch (error) {
              console.error(
                'Error mapping product:',
                rebuyProduct?.handle,
                error
              );
              return null;
            }
          })
        )
      ).filter(product => product !== null);

      return mappedProducts;
    } catch (error) {
      console.error('Error fetching Rebuy cart upsells:', error);
      return [];
    }
  }

  /**
   * Handles add to cart button click
   * @param {Event} e - Click event
   * @param {Object} variant - Selected variant data
   */
  async function handleAddToCart(e, selectedVariant, product) {
    e.preventDefault();
    const button = e.currentTarget;

    // Use the existing QuickCart addItemToCart functionality
    if (BSS.Components?.QuickCart?.addItemToCart) {
      await BSS.Components.QuickCart.addItemToCart(
        { id: selectedVariant?.id, properties: product?.properties || {} },
        button
      );
    } else {
      console.error('QuickCart component not available');
    }
    // klaviyo event
    createAndPushKlaviyoItem(product);

    // dataLayer event
    dataLayer.push({
      event: 'rebuyCart Rec Widget Add to Cart',
      rebuyProductID: product?.id,
      rebuyProductVariant: selectedVariant?.title,
      rebuyProductName: product?.handle
    });
  }

  /**
   * Finds the correct image source for a given variant
   * @param {Object} product - Product data with variant image mapping
   * @param {Object} variant - Selected variant
   * @returns {string} Image source URL
   */
  function getVariantImageSrc(product, variant) {
    if (!product || !variant) {
      return product?.featured_image || '';
    }

    // First try the pre-built variant image map (fastest)
    if (product.variantImageMap && product.variantImageMap.has(variant.id)) {
      return product.variantImageMap.get(variant.id);
    }

    // Fallback to variant's featured_image_url property
    if (variant.featured_image_url) {
      return variant.featured_image_url;
    }

    // Final fallback to product featured image
    return product?.featured_image || '';
  }

  /**
   * Updates the product card image based on selected variant
   * @param {HTMLElement} card - Product card element
   * @param {Object} product - Product data with variant image mapping
   * @param {Object} variant - Selected variant
   */
  function updateCardImage(card, product, variant) {
    const img = card.querySelector('.cart-upsell-card__image-container img');
    if (!img) return;

    const newImageSrc = safeImageUrl(getVariantImageSrc(product, variant));
    if (newImageSrc && newImageSrc !== img.src) {
      img.src = newImageSrc;
      img.alt = `${product?.title || 'Product'} - ${
        variant?.title || 'Variant'
      }`;
    }
  }

  /**
   * Creates a product card element for the carousel
   * @param {Object} product - Product data with embedded variant image mapping
   * @param {number} index - Card index for carousel ID
   * @returns {HTMLElement} Product card element
   */
  function createProductCard(product, index) {
    if (!product) {
      console.error('Invalid product data');
      return document.createElement('div');
    }

    const saleSettings = getSaleSettings();
    const isSaleEligible = hasSaleTag(product, saleSettings);

    const card = document.createElement('div');
    card.className = 'cart-upsell-card';
    card.id = `Carousel-Item-${product?.id ?? index}`;
    card.setAttribute('role', 'listitem');

    const money = (cents, digits = 2) =>
      `$${(Number(cents ?? 0) / 100).toFixed(digits)}`;
    const price =
      isSaleEligible && saleSettings?.isEnabled && product?.sale_price
        ? html`
            <span
              class="cart-upsell-card__amount cart-upsell-card__amount--compare"
            >
              ${money(product?.compare_at_price, 0)}
            </span>
            <span
              class="cart-upsell-card__amount cart-upsell-card__amount--sale"
            >
              ${money(product?.sale_price)}
            </span>
          `
        : html`
            ${product?.compare_at_price
              ? html`<span
                  class="cart-upsell-card__amount cart-upsell-card__amount--compare"
                >
                  ${money(product?.compare_at_price)}
                </span>`
              : ''}
            <span class="cart-upsell-card__amount">
              ${money(product?.price)}
            </span>
          `;
    const variantOptions = (product?.variants ?? []).map(
      variant => html`
        <option
          value="${variant?.id ?? ''}"
          ${!variant?.available ? 'disabled' : ''}
        >
          ${variant?.title ?? 'Variant'}${!variant?.available
            ? ' - Sold Out'
            : ''}
        </option>
      `
    );

    card.innerHTML = html`
      <div class="cart-upsell-card__image-container">
        <a
          href="${safeUrl(
            product?.selected_or_first_available_variant?.url ?? product?.url,
            { fallback: '#' }
          )}"
          tabindex="-1"
        >
          <img
            src="${safeImageUrl(product?.featured_image)}"
            alt="${product?.title ?? 'Product'}"
            loading="lazy"
          />
        </a>
      </div>
      <div class="cart-upsell-card__content">
        <h3 class="cart-upsell-card__title">
          <a
            href="${safeUrl(product?.url, { fallback: '#' })}"
            class="cart-upsell-card__details"
          >
            ${product?.title ?? 'Product'}
          </a>
        </h3>
        <div class="cart-upsell-card__price">${price}</div>
      </div>
      <div class="cart-upsell-card__options">
        <select class="cart-upsell-card__variant-select">
          ${variantOptions}
        </select>
        <div class="cart-upsell-card__actions-row">
          <button
            class="cart-upsell-card__add-button"
            data-product-id="${product?.id ?? ''}"
          >
            Add<span class="cart-upsell-card__add-button-text--desktop">
              to Bag</span
            >
          </button>
        </div>
      </div>
    `;

    // Attach event listeners
    const addButton = card.querySelector('.cart-upsell-card__add-button');
    const variantSelect = card.querySelector(
      '.cart-upsell-card__variant-select'
    );

    if (addButton && variantSelect) {
      // Add to cart functionality
      addButton.addEventListener('click', e => {
        const selectedVariantId = variantSelect.value;
        const selectedVariant = product?.variants?.find(
          v => v?.id?.toString() === selectedVariantId
        );

        if (selectedVariant) {
          handleAddToCart(e, selectedVariant, product);
        }
      });

      // Variant change functionality for image switching
      variantSelect.addEventListener('change', e => {
        const selectedVariantId = e.target.value;
        const selectedVariant = product?.variants?.find(
          v => v?.id?.toString() === selectedVariantId
        );

        if (selectedVariant) {
          updateCardImage(card, product, selectedVariant);
        }
      });
    }

    return card;
  }

  /**
   * Gets fresh Slick slider configuration for cart upsells
   * This function is called each time to ensure fresh DOM references
   */
  function getUpsellsSlickOptions() {
    return {
      infinite: true,
      slidesToShow: 1,
      slidesToScroll: 1,
      mobileFirst: true,
      prevArrow: $('.cart-upsells__nav-btn[name="previous"]'),
      nextArrow: $('.cart-upsells__nav-btn[name="next"]')
    };
  }

  const desktopUpsellsQuery = window.matchMedia('(min-width: 1200px)');
  let breakpointListenerAdded = false;

  /**
   * Synchronizes Slick with the cart upsells layout breakpoint.
   */
  function syncUpsellsSlider() {
    const upsellsCarousel = document.querySelector('.cart-upsells__items');
    if (!upsellsCarousel) return;

    if (!desktopUpsellsQuery.matches) {
      if (!$(upsellsCarousel).hasClass('slick-initialized')) {
        $(upsellsCarousel).slick(getUpsellsSlickOptions());
      }
    } else if ($(upsellsCarousel).hasClass('slick-initialized')) {
      $(upsellsCarousel).slick('unslick');
    }
  }

  /**
   * Renders cart upsell products into the carousel
   * @param {Array} products - Array of product objects with embedded rebuyProduct data
   */
  function renderUpsells(products) {
    const carouselItems = document.querySelector('.cart-upsells__items');
    const carouselWrapper = document.querySelector(
      '.cart-upsells-carousel-wrapper'
    );

    if (!carouselItems) {
      return;
    }

    // Destroy existing Slick instance if it exists
    if ($(carouselItems)?.hasClass('slick-initialized')) {
      $(carouselItems).slick('unslick');
    }

    // Clear skeleton items first, regardless of whether we have products
    carouselItems.innerHTML = '';

    // If no products or invalid products array, hide the wrapper and return
    if (!Array.isArray(products) || !products.length) {
      if (carouselWrapper) {
        carouselWrapper.style.display = 'none';
      }
      return;
    }

    if (carouselWrapper) {
      carouselWrapper.style.display = 'block';
    }

    // Add product cards
    products.forEach((product, index) => {
      const card = createProductCard(product, index);
      carouselItems.appendChild(card);
    });

    syncUpsellsSlider();

    if (!breakpointListenerAdded) {
      desktopUpsellsQuery.addEventListener('change', syncUpsellsSlider);
      breakpointListenerAdded = true;
    }
  }

  /**
   * Initializes cart upsells
   */
  async function init() {
    // Prevent duplicate simultaneous initialization
    if (isInitializing) {
      return;
    }

    isInitializing = true;

    // Check if we're in the cart and it has items
    const carouselWrapper = document.querySelector(
      '.cart-upsells-carousel-wrapper'
    );

    try {
      const products = await fetchUpsells();

      renderUpsells(products);
    } catch (error) {
      console.error('❌ Error initializing cart upsells:', error);
      // Hide carousel on error
      if (carouselWrapper) {
        carouselWrapper.style.display = 'none';
      }
    } finally {
      isInitializing = false;
    }
  }

  return {
    init,
    fetchUpsells,
    renderUpsells
  };
})();
